'use client';

import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';

import type { StateAbbreviation } from '@ria-client/supergraph/marketplace/types';
import { getSafeRegionCodeFromClientCookies } from '@ria-client/utils/geo-data';

type GeoContext = {
  isReady: boolean;
  regionCode: StateAbbreviation | undefined;
  setRegionCode: (code: StateAbbreviation) => void;
  initialRegionCode: StateAbbreviation | undefined;
};

const GeoContext = createContext<GeoContext | undefined>(undefined);

export interface GeoProviderProps {
  children: ReactNode;
  initialLocation?: StateAbbreviation;
}

export function GeoProvider({ children, initialLocation }: GeoProviderProps) {
  const [isReady, setIsReady] = useState(false);
  const [regionCode, setRegionCode] = useState<StateAbbreviation | undefined>(initialLocation);
  const [initialRegionCode, setInitialRegionCode] = useState<StateAbbreviation | undefined>(
    initialLocation,
  );

  useEffect(() => {
    const clientRegionCode = getSafeRegionCodeFromClientCookies();
    if (clientRegionCode) {
      setRegionCode(clientRegionCode);
      setInitialRegionCode(clientRegionCode);
    }
    setIsReady(true);
  }, []);

  return (
    <GeoContext.Provider value={{ isReady, regionCode, setRegionCode, initialRegionCode }}>
      {children}
    </GeoContext.Provider>
  );
}

export function useGeo(): GeoContext {
  const context = useContext(GeoContext);

  if (!context) {
    throw new Error('useGeo must be used within a GeoProvider');
  }

  return context;
}
